import { cva, type VariantProps } from "class-variance-authority"
import {
  ArrowRight,
  Ban,
  BarChart3,
  Check,
  ChevronDown,
  ChevronsUpDown,
  Copy,
  CornerDownRight,
  CreditCard,
  Edit,
  ExternalLink,
  HelpCircle,
  MapPin,
  Menu,
  Minus,
  Plus,
  QrCode,
  RefreshCw,
  Search,
  SortAsc,
  Ticket,
  Trash2,
  Undo,
  Upload,
  User,
  UserCog,
  Users,
  X,
} from "lucide-react"
import type * as React from "react"

import { cn } from "../lib/utils"

const iconVariants = cva("shrink-0", {
  variants: {
    size: {
      sm: "size-4",
      default: "size-6",
      lg: "size-8",
    },
  },
  defaultVariants: {
    size: "default",
  },
})

export type IconName =
  | "close"
  | "menu"
  | "more"
  | "backward"
  | "forward"
  | "add"
  | "remove"
  | "check"
  | "change"
  | "ticket"
  | "card"
  | "apple-pay"
  | "paypay"
  | "google"
  | "apple"
  | "instagram"
  | "x"
  | "line"
  | "search"
  | "account"
  | "open"
  | "manager"
  | "staff"
  | "upload"
  | "qr"
  | "pin"
  | "duplicate"
  | "delete"
  | "select"
  | "edit"
  | "analytics"
  | "help"
  | "sort"
  | "subdirectory"
  | "ban"
  | "undo"
  | "explore"
  | "confirmation-number"
  | "face"
  | "block"
  | "chevron-forward"

export interface IconProps
  extends Omit<React.SVGAttributes<SVGElement>, "title">,
    VariantProps<typeof iconVariants> {
  name: IconName
  title?: string
  fillColor?: string
}

type CustomIconProps = Omit<React.SVGAttributes<SVGElement>, "title"> & {
  title?: string
  fillColor?: string
}

const resolveFillColor = (fillColor?: string) => fillColor ?? "currentColor"

const BackwardIcon = ({ title, fillColor, ...props }: CustomIconProps) => (
  <svg viewBox="0 0 6.55 11.15" fill="none" {...props}>
    <title>{title}</title>
    <path
      d="M2.375 5.575L6.275 9.475C6.45833 9.65833 6.55 9.89167 6.55 10.175C6.55 10.4583 6.45833 10.6917 6.275 10.875C6.09167 11.0583 5.85833 11.15 5.575 11.15C5.29167 11.15 5.05833 11.0583 4.875 10.875L0.275 6.275C0.175 6.175 0.104167 6.06667 0.0625 5.95C0.0208333 5.83333 0 5.70833 0 5.575C0 5.44167 0.0208333 5.31667 0.0625 5.2C0.104167 5.08333 0.175 4.975 0.275 4.875L4.875 0.275C5.05833 0.0916667 5.29167 0 5.575 0C5.85833 0 6.09167 0.0916667 6.275 0.275C6.45833 0.458333 6.55 0.691667 6.55 0.975C6.55 1.25833 6.45833 1.49167 6.275 1.675L2.375 5.575Z"
      fill={resolveFillColor(fillColor)}
    />
  </svg>
)

// Brand icon components
const GoogleIcon = ({ title, ...props }: CustomIconProps) => (
  <svg viewBox="0 0 24 24" fill="none" {...props}>
    <title>{title}</title>
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
)

const AppleIcon = ({ title, fillColor, ...props }: CustomIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <title>{title}</title>
    <path
      d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"
      fill={resolveFillColor(fillColor)}
    />
  </svg>
)

const ApplePayIcon = ({ title, fillColor, ...props }: CustomIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <title>{title}</title>
    <path
      d="M4.04 9.6c-.28.37-.72.66-.72.66s.38-.55.53-.75c.42-.55.71-.88.71-.88s-.24.6-.52.97zm2.03-1.47c-.44 0-.81.19-1.03.19-.23 0-.58-.18-.95-.18-.49 0-.94.28-1.19.72-.51.88-.13 2.18.36 2.9.24.35.53.75.91.73.37-.02.5-.24.94-.24s.57.24.96.23c.39 0 .65-.36.89-.71.28-.41.4-.8.4-.82 0-.02-.77-.3-.78-1.18 0-.74.61-1.09.63-1.11-.34-.5-.88-.56-1.07-.57 0 .01-.38.02-.07.04zM9 5.76c.22-.27.5-.48.5-.48s-.04.31-.26.6c-.25.33-.52.51-.52.51s.06-.34.28-.63zm3.84 5.13v4.86h-1.5v-6.5h1.47v.85c.35-.58.93-.96 1.72-.96 1.28 0 2.16.98 2.16 2.4v4.21h-1.5v-3.98c0-.83-.43-1.35-1.15-1.35-.69 0-1.2.55-1.2 1.35v3.98h-.01zm5.7 4.86h1.5l2.39-6.5h-1.54l-1.51 4.68-1.51-4.68H16.3l2.24 6.5z"
      fill={resolveFillColor(fillColor)}
    />
  </svg>
)

const PayPayIcon = ({ title, ...props }: CustomIconProps) => (
  <svg viewBox="0 0 24 24" {...props}>
    <title>{title}</title>
    <circle cx="12" cy="12" r="11" fill="#FF0033" />
    <path
      d="M7.5 8h2.2c1.3 0 2.3.9 2.3 2.1 0 1.2-1 2.1-2.3 2.1H8.5v2.3H7.5V8zm1 3.2h1.1c.8 0 1.3-.4 1.3-1.1 0-.6-.5-1.1-1.3-1.1H8.5v2.2z"
      fill="white"
    />
    <path
      d="M13.5 8h2.2c1.3 0 2.3.9 2.3 2.1 0 1.2-1 2.1-2.3 2.1h-1.2v2.3h-1V8zm1 3.2h1.1c.8 0 1.3-.4 1.3-1.1 0-.6-.5-1.1-1.3-1.1h-1.1v2.2z"
      fill="white"
    />
  </svg>
)

const InstagramIcon = ({ title, ...props }: CustomIconProps) => (
  <svg viewBox="0 0 24 24" {...props}>
    <title>{title}</title>
    <defs>
      <radialGradient id="instagram-gradient" cx="30%" cy="107%" r="150%">
        <stop offset="0%" stopColor="#fdf497" />
        <stop offset="5%" stopColor="#fdf497" />
        <stop offset="45%" stopColor="#fd5949" />
        <stop offset="60%" stopColor="#d6249f" />
        <stop offset="90%" stopColor="#285AEB" />
      </radialGradient>
    </defs>
    <path
      fill="url(#instagram-gradient)"
      d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"
    />
  </svg>
)

const XIcon = ({ title, fillColor, ...props }: CustomIconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <title>{title}</title>
    <path
      d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
      fill={resolveFillColor(fillColor)}
    />
  </svg>
)

const LineIcon = ({ title, ...props }: CustomIconProps) => (
  <svg viewBox="0 0 24 24" fill="#00B900" {...props}>
    <title>{title}</title>
    <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
  </svg>
)

const ExploreIcon = ({ title, fillColor, ...props }: CustomIconProps) => (
  <svg viewBox="0 0 28 28" fill="none" {...props}>
    <title>{title}</title>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M13.0658 3.7334C18.2204 3.7334 22.3991 7.91207 22.3991 13.0667C22.3991 15.2234 21.6645 17.2069 20.436 18.7873L23.8073 22.1585C24.2629 22.6141 24.2628 23.3527 23.8073 23.8083C23.3517 24.2639 22.6132 24.2639 22.1576 23.8083L18.7863 20.437C17.2059 21.6654 15.2225 22.4001 13.0658 22.4001C7.9111 22.4001 3.73242 18.2214 3.73242 13.0667C3.73242 7.91207 7.9111 3.7334 13.0658 3.7334ZM13.0658 6.06673C9.19976 6.06673 6.06576 9.20074 6.06576 13.0667C6.06576 16.9327 9.19976 20.0667 13.0658 20.0667C16.9317 20.0667 20.0658 16.9327 20.0658 13.0667C20.0658 9.20074 16.9317 6.06673 13.0658 6.06673Z"
      fill={resolveFillColor(fillColor)}
    />
  </svg>
)

const ConfirmationNumberIcon = ({ title, fillColor, ...props }: CustomIconProps) => (
  <svg viewBox="0 0 28 28" fill="none" {...props}>
    <title>{title}</title>
    <path
      d="M4.66732 23.3337C4.02565 23.3337 3.47635 23.1052 3.0194 22.6482C2.56246 22.1913 2.33398 21.642 2.33398 21.0003V17.0628C2.33398 16.8489 2.40204 16.6642 2.53815 16.5087C2.67426 16.3531 2.84926 16.2559 3.06315 16.217C3.52982 16.0614 3.91385 15.7795 4.21523 15.3712C4.51662 14.9628 4.66732 14.5059 4.66732 14.0003C4.66732 13.4948 4.51662 13.0378 4.21523 12.6295C3.91385 12.2212 3.52982 11.9392 3.06315 11.7837C2.84926 11.7448 2.67426 11.6475 2.53815 11.492C2.40204 11.3364 2.33398 11.1517 2.33398 10.9378V7.00033C2.33398 6.35866 2.56246 5.80935 3.0194 5.35241C3.47635 4.89546 4.02565 4.66699 4.66732 4.66699H23.334C23.9757 4.66699 24.525 4.89546 24.9819 5.35241C25.4388 5.80935 25.6673 6.35866 25.6673 7.00033V10.9378C25.6673 11.1517 25.5993 11.3364 25.4632 11.492C25.327 11.6475 25.152 11.7448 24.9382 11.7837C24.4715 11.9392 24.0875 12.2212 23.7861 12.6295C23.4847 13.0378 23.334 13.4948 23.334 14.0003C23.334 14.5059 23.4847 14.9628 23.7861 15.3712C24.0875 15.7795 24.4715 16.0614 24.9382 16.217C25.152 16.2559 25.327 16.3531 25.4632 16.5087C25.5993 16.6642 25.6673 16.8489 25.6673 17.0628V21.0003C25.6673 21.642 25.4388 22.1913 24.9819 22.6482C24.525 23.1052 23.9757 23.3337 23.334 23.3337H4.66732ZM4.66732 21.0003H23.334V18.0253C22.6145 17.5975 22.0458 17.0288 21.6277 16.3191C21.2097 15.6094 21.0007 14.8364 21.0007 14.0003C21.0007 13.1642 21.2097 12.3913 21.6277 11.6816C22.0458 10.9719 22.6145 10.4031 23.334 9.97533V7.00033H4.66732V9.97533C5.38676 10.4031 5.95551 10.9719 6.37357 11.6816C6.79162 12.3913 7.00065 13.1642 7.00065 14.0003C7.00065 14.8364 6.79162 15.6094 6.37357 16.3191C5.95551 17.0288 5.38676 17.5975 4.66732 18.0253V21.0003ZM14.0007 19.8337C14.3312 19.8337 14.6083 19.7219 14.8319 19.4982C15.0555 19.2746 15.1673 18.9975 15.1673 18.667C15.1673 18.3364 15.0555 18.0594 14.8319 17.8357C14.6083 17.6121 14.3312 17.5003 14.0007 17.5003C13.6701 17.5003 13.393 17.6121 13.1694 17.8357C12.9458 18.0594 12.834 18.3364 12.834 18.667C12.834 18.9975 12.9458 19.2746 13.1694 19.4982C13.393 19.7219 13.6701 19.8337 14.0007 19.8337ZM14.0007 15.167C14.3312 15.167 14.6083 15.0552 14.8319 14.8316C15.0555 14.608 15.1673 14.3309 15.1673 14.0003C15.1673 13.6698 15.0555 13.3927 14.8319 13.1691C14.6083 12.9455 14.3312 12.8337 14.0007 12.8337C13.6701 12.8337 13.393 12.9455 13.1694 13.1691C12.9458 13.3927 12.834 13.6698 12.834 14.0003C12.834 14.3309 12.9458 14.608 13.1694 14.8316C13.393 15.0552 13.6701 15.167 14.0007 15.167ZM14.0007 10.5003C14.3312 10.5003 14.6083 10.3885 14.8319 10.1649C15.0555 9.9413 15.1673 9.66421 15.1673 9.33366C15.1673 9.0031 15.0555 8.72602 14.8319 8.50241C14.6083 8.2788 14.3312 8.16699 14.0007 8.16699C13.6701 8.16699 13.393 8.2788 13.1694 8.50241C12.9458 8.72602 12.834 9.0031 12.834 9.33366C12.834 9.66421 12.9458 9.9413 13.1694 10.1649C13.393 10.3885 13.6701 10.5003 14.0007 10.5003Z"
      fill={resolveFillColor(fillColor)}
    />
  </svg>
)

const FaceIcon = ({ title, fillColor, ...props }: CustomIconProps) => (
  <svg viewBox="0 0 28 28" fill="none" {...props}>
    <title>{title}</title>
    <path
      d="M10.5007 16.6247C10.0923 16.6247 9.74718 16.4837 9.46524 16.2018C9.18329 15.9198 9.04232 15.5747 9.04232 15.1663C9.04232 14.758 9.18329 14.4129 9.46524 14.1309C9.74718 13.849 10.0923 13.708 10.5007 13.708C10.909 13.708 11.2541 13.849 11.5361 14.1309C11.818 14.4129 11.959 14.758 11.959 15.1663C11.959 15.5747 11.818 15.9198 11.5361 16.2018C11.2541 16.4837 10.909 16.6247 10.5007 16.6247ZM17.5007 16.6247C17.0923 16.6247 16.7472 16.4837 16.4652 16.2018C16.1833 15.9198 16.0423 15.5747 16.0423 15.1663C16.0423 14.758 16.1833 14.4129 16.4652 14.1309C16.7472 13.849 17.0923 13.708 17.5007 13.708C17.909 13.708 18.2541 13.849 18.5361 14.1309C18.818 14.4129 18.959 14.758 18.959 15.1663C18.959 15.5747 18.818 15.9198 18.5361 16.2018C18.2541 16.4837 17.909 16.6247 17.5007 16.6247ZM14.0007 23.333C16.6062 23.333 18.8132 22.4288 20.6215 20.6205C22.4298 18.8122 23.334 16.6052 23.334 13.9997C23.334 13.533 23.3048 13.0809 23.2465 12.6434C23.1882 12.2059 23.0812 11.783 22.9257 11.3747C22.5173 11.4719 22.109 11.5448 21.7007 11.5934C21.2923 11.642 20.8645 11.6663 20.4173 11.6663C18.6479 11.6663 16.9757 11.2872 15.4007 10.5288C13.8257 9.77051 12.484 8.71079 11.3757 7.34968C10.7534 8.86634 9.86385 10.1837 8.7069 11.3018C7.54996 12.4198 6.20343 13.2608 4.66732 13.8247V13.9997C4.66732 16.6052 5.57148 18.8122 7.37982 20.6205C9.18815 22.4288 11.3951 23.333 14.0007 23.333ZM14.0007 25.6663C12.3868 25.6663 10.8701 25.3601 9.45065 24.7476C8.03121 24.1351 6.79648 23.3038 5.74648 22.2538C4.69648 21.2038 3.86523 19.9691 3.25273 18.5497C2.64023 17.1302 2.33398 15.6136 2.33398 13.9997C2.33398 12.3858 2.64023 10.8691 3.25273 9.44968C3.86523 8.03023 4.69648 6.79551 5.74648 5.74551C6.79648 4.69551 8.03121 3.86426 9.45065 3.25176C10.8701 2.63926 12.3868 2.33301 14.0007 2.33301C15.6145 2.33301 17.1312 2.63926 18.5507 3.25176C19.9701 3.86426 21.2048 4.69551 22.2548 5.74551C23.3048 6.79551 24.1361 8.03023 24.7486 9.44968C25.3611 10.8691 25.6673 12.3858 25.6673 13.9997C25.6673 15.6136 25.3611 17.1302 24.7486 18.5497C24.1361 19.9691 23.3048 21.2038 22.2548 22.2538C21.2048 23.3038 19.9701 24.1351 18.5507 24.7476C17.1312 25.3601 15.6145 25.6663 14.0007 25.6663ZM12.4257 4.81217C13.2423 6.17329 14.3507 7.26704 15.7507 8.09342C17.1507 8.91981 18.7062 9.33301 20.4173 9.33301C20.6895 9.33301 20.952 9.31843 21.2048 9.28926C21.4576 9.26009 21.7201 9.22606 21.9923 9.18717C21.1757 7.82606 20.0673 6.73231 18.6673 5.90592C17.2673 5.07954 15.7118 4.66634 14.0007 4.66634C13.7284 4.66634 13.4659 4.68092 13.2132 4.71009C12.9604 4.73926 12.6979 4.77329 12.4257 4.81217ZM5.16315 11.0538C6.15482 10.49 7.0201 9.76079 7.75899 8.86634C8.49787 7.9719 9.05204 6.97051 9.42149 5.86217C8.42982 6.42606 7.56454 7.15523 6.82565 8.04967C6.08676 8.94412 5.5326 9.94551 5.16315 11.0538Z"
      fill={resolveFillColor(fillColor)}
    />
  </svg>
)

const BlockIcon = ({ title, fillColor, ...props }: CustomIconProps) => (
  <svg viewBox="0 0 24 24" fill="none" {...props}>
    <title>{title}</title>
    <path
      d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM4 12C4 10.15 4.63 8.45 5.69 7.1L16.9 18.31C15.55 19.37 13.85 20 12 20C7.58 20 4 16.42 4 12ZM18.31 16.9L7.1 5.69C8.45 4.63 10.15 4 12 4C16.42 4 20 7.58 20 12C20 13.85 19.37 15.55 18.31 16.9Z"
      fill={resolveFillColor(fillColor)}
    />
  </svg>
)

const ChevronForwardIcon = ({ title, fillColor, ...props }: CustomIconProps) => (
  <svg viewBox="0 0 24 24" fill="none" {...props}>
    <title>{title}</title>
    <path
      d="M8.58804 16.59L13.168 12L8.58804 7.41L10.0014 6L16.0014 12L10.0014 18L8.58804 16.59Z"
      fill={resolveFillColor(fillColor)}
    />
  </svg>
)

const iconMap: Record<IconName, React.ComponentType<CustomIconProps>> = {
  close: X,
  menu: Menu,
  more: ChevronDown,
  backward: BackwardIcon,
  forward: ArrowRight,
  add: Plus,
  remove: Minus,
  check: Check,
  change: RefreshCw,
  ticket: Ticket,
  card: CreditCard,
  "apple-pay": ApplePayIcon,
  paypay: PayPayIcon,
  google: GoogleIcon,
  apple: AppleIcon,
  instagram: InstagramIcon,
  x: XIcon,
  line: LineIcon,
  search: Search,
  account: User,
  open: ExternalLink,
  manager: UserCog,
  staff: Users,
  upload: Upload,
  qr: QrCode,
  pin: MapPin,
  duplicate: Copy,
  delete: Trash2,
  select: ChevronsUpDown,
  edit: Edit,
  analytics: BarChart3,
  help: HelpCircle,
  sort: SortAsc,
  subdirectory: CornerDownRight,
  ban: Ban,
  undo: Undo,
  explore: ExploreIcon,
  "confirmation-number": ConfirmationNumberIcon,
  face: FaceIcon,
  block: BlockIcon,
  "chevron-forward": ChevronForwardIcon,
}

const Icon = ({ name, size, className, ...props }: IconProps) => {
  const IconComponent = iconMap[name]

  if (!IconComponent) {
    console.warn(`Icon "${name}" not found`)
    return null
  }

  const title = props.title ?? `${name} icon`

  return (
    <IconComponent className={cn(iconVariants({ size, className }))} {...props} title={title} />
  )
}

export { Icon, iconVariants }
